import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_4935773c-183d-4ff8-bc9d-3030c509887e/artifacts/mknxgjj1_identra_logo_3d_clean_hd.jpg";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-container">
        <div className="auth-form-side">
          <div className="max-w-md mx-auto w-full">
            <Link to="/" className="flex items-center gap-3 mb-12">
              <img src={LOGO_URL} alt="Identra" className="h-12 w-auto" />
            </Link>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-3">Check your email</h1>
              <p className="text-muted-foreground text-lg mb-8">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              
              <div className="space-y-4">
                <Button 
                  variant="outline"
                  className="w-full h-12"
                  onClick={() => setSubmitted(false)}
                  data-testid="try-again-btn"
                >
                  Try another email
                </Button>
                <Link to="/login">
                  <Button 
                    variant="ghost"
                    className="w-full h-12"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-visual-identra">
          <div className="relative z-10 text-center text-white">
            <Mail className="w-20 h-20 mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl font-bold mb-4">Email Sent!</h2>
            <p className="text-white/80 max-w-sm mx-auto">
              Follow the instructions in your email to reset your password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form-side">
        <div className="max-w-md mx-auto w-full">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src={LOGO_URL} alt="Identra" className="h-12 w-auto" />
          </Link>

          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-3">Forgot password?</h1>
            <p className="text-muted-foreground text-lg">
              No worries, we'll send you reset instructions.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 text-base"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold bg-[#16335E] hover:bg-[#0A1A34]"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <div className="loading-spinner w-5 h-5" />
              ) : (
                <>
                  Send reset link
                  <Send className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center">
            <Link 
              to="/login" 
              className="text-muted-foreground hover:text-foreground inline-flex items-center"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to login
            </Link>
          </p>
        </div>
      </div>

      <div className="auth-visual-identra">
        <div className="relative z-10 text-center text-white">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Password Recovery</h2>
          <p className="text-white/80 max-w-sm mx-auto">
            Enter your email and we'll send you a link to reset your password securely.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
